import * as RadixDialog from '@radix-ui/react-dialog'
import type { ReactNode } from 'react'

import { Button } from './Button.tsx'

/**
 * Painel lateral.
 *
 * Uma ficha de investigação não cabe numa linha expandida sem espremer os pivôs
 * (§6, §21), e não é um formulário modal centralizado. É um painel à direita, de
 * altura cheia, que rola sozinho e deixa a lista visível atrás — o operador abre
 * um evento, lê o contexto, pivota, e a lista continua ali.
 *
 * Reaproveita o `Dialog` do Radix pelo mesmo motivo: foco preso, devolução do
 * foco ao fechar, `Escape` e o resto da árvore inerte. Sombra e nenhuma borda,
 * como as demais superfícies elevadas do console.
 */
export function Drawer({
  open,
  onOpenChange,
  title,
  description,
  meta,
  children,
  footer,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: ReactNode
  description?: ReactNode
  /** Canto direito do cabeçalho, ao lado do fechar: um selo, uma contagem. */
  meta?: ReactNode
  children: ReactNode
  footer?: ReactNode
}) {
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-50 bg-canvas/75" />
        <RadixDialog.Content
          className="fixed inset-y-0 right-0 z-50 flex w-[calc(100vw-2rem)] max-w-xl flex-col bg-raised shadow-overlay"
          aria-describedby={undefined}
        >
          <header className="flex shrink-0 items-start justify-between gap-3 border-b border-line-subtle px-4 py-2.5">
            <div className="min-w-0 flex-1">
              <RadixDialog.Title className="text-base font-medium text-fg">{title}</RadixDialog.Title>
              {description !== undefined && (
                <div className="mt-0.5 text-micro text-fg-muted">{description}</div>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {meta}
              <RadixDialog.Close asChild>
                <Button variant="ghost" size="sm" aria-label="Fechar">
                  ✕
                </Button>
              </RadixDialog.Close>
            </div>
          </header>

          <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>

          {footer !== undefined && (
            <div className="flex shrink-0 flex-wrap items-center gap-2 border-t border-line-subtle px-4 py-2.5">
              {footer}
            </div>
          )}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  )
}
